import type { ModelBase } from './modelBase';

export interface Input extends ModelBase {
  name: string;
  enabled: boolean;
  createdDate?: string;
  modifiedDate?: string;
  identifier: string;
  actions?: [];
  scheduleId?: string | null;
  entityOuId?: string;
  deleted?: boolean;
  scheduleICSData?: string | null;
  description?: string | null;
  /** Server-computed OverallStatus: Disconnected | Connected | Recording. */
  overallStatus?: string;
  /** Scheduled Input↔LiveProgram links. */
  liveProgramIds?: string[];
  /** Programs currently Recording (fullInfo / status API). */
  currentLiveProgramIds?: string[];
  ingestUrl?: string;
  streamKey?: string;
  /** Maps to InputDto.SourceStatus (SourceStatusTypes). */
  sourceStatus?: SourceStatusTypes | string;
  color?: string | null;
  location?: string | null;
  protocol?: string | null;
}

export interface InputMetaData {
  location: string;
  protocol: string;
  resolution?: string;
}

/** Maps to FuelTools.Core.Shared.Models.SourceStatusTypes */
export enum SourceStatusTypes {
  Disconnected = 'Disconnected',
  Connected = 'Connected',
}

/** Maps to FuelTools.Core.Shared.Models.RecordingStatusTypes / inferred Input overall when recording. */
export enum RecordingStatusTypes {
  NotRecording = 'NotRecording',
  Recording = 'Recording',
}

export interface InputLiveStatusResponse {
  inputId?: string;
  overallStatus?: string;
  sourceStatus?: string;
  liveProgramIds?: string[];
  currentLiveProgramIds?: string[];
}

/** API overallStatus values. UI may display Recording as "Live". */
export enum InputOverallStatus {
  Disconnected = 'Disconnected',
  Connected = 'Connected',
  Recording = 'Recording',
}

export interface InputDisplayStatus {
  showLiveBadge: boolean;
  label: string;
}

export function isInputOverallStatusDisconnected(overallStatus?: string | null): boolean {
  return overallStatus?.toLowerCase() === InputOverallStatus.Disconnected.toLowerCase();
}

/** True when overall status is Recording (UI treats this as Live). */
export function isInputOverallStatusLive(overallStatus?: string | null): boolean {
  return overallStatus?.toLowerCase() === InputOverallStatus.Recording.toLowerCase();
}

/** True when a recording session is active. */
export function isInputOverallStatusLiveSessionActive(overallStatus?: string | null): boolean {
  return isInputOverallStatusLive(overallStatus);
}

/** True when the input source is available for preview (Connected or Recording). */
export function isInputOverallStatusConnected(overallStatus?: string | null): boolean {
  if (!overallStatus) {
    return false;
  }
  const normalized = overallStatus.toLowerCase();
  return (
    normalized === InputOverallStatus.Connected.toLowerCase() ||
    normalized === InputOverallStatus.Recording.toLowerCase()
  );
}

/**
 * Encoder ingest connection for display and preview gating.
 * `sourceStatus` from the API is authoritative when present.
 */
export function resolveInputSourceConnectionStatus(
  sourceStatus?: string | null,
  overallStatus?: string | null,
): string | undefined {
  if (sourceStatus?.trim()) {
    return sourceStatus;
  }
  if (isInputOverallStatusLive(overallStatus)) {
    return InputOverallStatus.Connected;
  }
  return overallStatus ?? undefined;
}

/** Badge label input: prefer OverallStatus; fall back to source connection when absent. */
export function resolveInputSourceBadgeStatus(
  sourceStatus?: string | null,
  overallStatus?: string | null,
): string | undefined {
  if (overallStatus?.trim()) {
    return overallStatus;
  }
  return resolveInputSourceConnectionStatus(sourceStatus, overallStatus);
}

export function isInputEncoderConnected(
  sourceStatus?: string | null,
  overallStatus?: string | null,
): boolean {
  const resolved = resolveInputSourceConnectionStatus(sourceStatus, overallStatus);
  return resolved?.toLowerCase() === InputOverallStatus.Connected.toLowerCase();
}

export function getInputDisplayStatus(overallStatus?: string | null): InputDisplayStatus {
  if (isInputOverallStatusLive(overallStatus)) {
    return { showLiveBadge: true, label: 'Live' };
  }

  if (overallStatus?.toLowerCase() === InputOverallStatus.Connected.toLowerCase()) {
    return { showLiveBadge: false, label: 'Connected' };
  }

  if (isInputOverallStatusDisconnected(overallStatus)) {
    return { showLiveBadge: false, label: 'No Connection' };
  }

  return { showLiveBadge: false, label: '' };
}

/** Brief debug log for overallStatus → display label mapping. */
export function logInputOverallStatus(context: string, overallStatus?: string | null): void {
  const displayStatus = getInputDisplayStatus(overallStatus);
  console.log(`[InputStatus] ${context}`, {
    overallStatus,
    label: displayStatus.label,
    showLiveBadge: displayStatus.showLiveBadge,
  });
}

export interface LivePreviewStartRequest {
  inputId: string;
  ouId: string;
}

export interface LivePreviewStartResponse {
  jobId: string;
  livePreviewUrl: string;
}

export interface LivePreviewHeartbeatResponse {
  jobId: string;
  livePreviewUrl: string;
}
